<template>
  <div v-if="model.state.gameValid" class="quiz" :class="quizContainerClasses">
    <div class="quiz__questions">
      <Flow>
        <FlowPage v-for="(introduction, index) in introductions" :key="index">
          <QuizQuestionIntroduction
            :custom-labels="model.state.general.customLabels"
            :introduction="introduction.question.introduction"
            @closeIntroduction="onCloseIntroduction(introduction.question.id)"
          />
        </FlowPage>
        <FlowPage
          v-for="question in shownQuestions"
          :key="question.id"
          :animation-enter="{ name: 'fadeIn', duration: 625, delay: 300 }"
        >
          <QuizQuestionItem
            :question="question"
            :loading="gameOver || isAnswering"
            :initial-selected-answer="questionAnswers.find((answered) => answered.question.id === question.id)?.answer"
            :active-question-index="activeQuestionIndex"
            :previous-button-label="question.previousQuestionLabel"
            :is-question-last="activeQuestionIndex === availableQuestions.length - 1"
            :general-settings="model.state.general"
            @stopTimer="onStopTimer"
            @previousQuestion="onPreviousQuestion(question.id)"
            @questionAnswer="onQuestionAnswer"
          />
        </FlowPage>

        <FlowPage v-for="(feedback, index) in feedbackMessages" :key="index">
          <QuizQuestionFeedback
            :has-correct-answer="hasCorrectAnswer"
            :feedback="feedback.question.feedback"
            :index="activeQuestionIndex"
            :previous-button-label="feedback.question.previousQuestionLabel"
            :can-navigate-back="model.state.general.goBackwards?.enabled"
            :is-question-last="activeQuestionIndex === availableQuestions.length - 1"
            :show-background-overlay="activeQuestion?.backgroundImage.onFeedback"
            @previousQuestion="onPreviousQuestion(feedback.question.id)"
            @nextQuestion="onNextQuestion(feedback.question)"
          />
        </FlowPage>
      </Flow>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType, Raw } from 'vue';
import { computed, defineComponent, onBeforeUnmount, ref, watch } from 'vue';
import QuizQuestionItem from './QuestionItem.vue';
import { SdkQuizQuestionTimesUpEvent } from './sdk/events/question-times-up';
import { SdkQuizQuestionModel } from './sdk/question-model';
import { SdkQuizQuestionFeedbackDisplayEvent } from './sdk/events/question-feedback-display';
import { SdkQuizQuestionAnswerModel } from './sdk/question-answer-model';
import type { GameQuizAnswerState, GameQuizQuestionState, GameQuizModel } from '@/src/components/games/quiz/Model';
import { QuizAnswerTypes, QuizGameTypes } from '@/src/components/games/quiz/Model';
import Flow from '@/src/components/Flow/Flow.vue';
import FlowPage from '@/src/components/Flow/FlowPage.vue';
import QuizQuestionIntroduction from '@/src/components/games/quiz/QuestionIntroduction.vue';
import QuizQuestionFeedback from '@/src/components/games/quiz/QuestionFeedback.vue';
import useEndGame from '@/src/hooks/useEndGame';
import { AnswerBulletsTypes, AnswerDesignTypes } from '@/src/models/settings/AnswerLayoutSettingsModel';
import { registerRepeatable, updateRepeatable } from '@/src/services/repeatable';
import { shuffle } from '@/src/utilities/Utilities';
import {
  buildAnswersAdditional,
  buildAnswersFlowOutput,
  buildResultOutput
} from '@/src/components/games/quiz/QuizUtilities';
import type { MetricData, ReplacementTags } from '@/src/store/campaign';
import { useCampaignStore } from '@/src/store/campaign';
import { useUtilityStore } from '@/src/store/utility';
import { useGameStepsStore } from '@/src/store/gamesteps';
import { SdkGameEventsEmitter } from '@/src/sdk/api/game/events';
import type { RepeatableData } from '@/src/store/repeatable';

type GameQuizRepeatableType = 'wrong_answers' | 'all_answers' | 'correct_answers';

interface QuizActiveQuestion {
  displayVariant: QuestionDisplayVariant;
  question: GameQuizQuestionState;
}

export interface GameQuizQuestionAnswer {
  answer: GameQuizAnswerState[];
  question: GameQuizQuestionState;
}

enum QuestionDisplayVariant {
  INTRODUCTION,
  QUESTION,
  FEEDBACK
}

export default defineComponent({
  name: 'GameQuiz',
  components: { QuizQuestionFeedback, QuizQuestionIntroduction, QuizQuestionItem, Flow, FlowPage },
  props: {
    model: {
      type: Object as PropType<GameQuizModel>,
      required: true
    }
  },
  setup(props) {
    const campaignStore = useCampaignStore();
    const utilityStore = useUtilityStore();
    const gameStepsStore = useGameStepsStore();

    // See userAnsweredQuestions computed before use
    const questionAnswers = ref<Raw<GameQuizQuestionAnswer[]>>([]);
    const activeQuestions = ref<Raw<QuizActiveQuestion[]>>([]);
    const timeUsed = ref(0);
    const activeQuestionIndex = ref(0);
    const stepIndex = ref(0);
    const isAnswering = ref(false);
    const timeLeft = ref(props.model.state.general.time.limit ? props.model.state.general.time.limit : 60);
    const gameOver = ref(false);
    const gameStartTime = ref<number | undefined>();
    const gameEndTime = ref<number | undefined>();

    let questionTimer: null | number = null;
    let gameTimer: null | number = null;

    const getAvailableQuestions = () => {
      let questions = props.model.state.questions.filter((question) => {
        if (question.date.enabled && question?.visibilityCondition) {
          return question.visibilityCondition.check();
        } else {
          return true;
        }
      });

      // if randomize question / all
      if (
        props.model.state.general.gameType === QuizGameTypes.RANDOMIZE ||
        props.model.state.general.gameType === QuizGameTypes.RANDOMIZE_ALL
      ) {
        const slice = props.model.state.general.questionsShown ?? 0;
        questions = shuffle(questions).slice(-slice);
      }

      // if all we also randomize the answers
      if (props.model.state.general.gameType === QuizGameTypes.RANDOMIZE_ALL) {
        questions = questions.map((question) => ({
          ...question,
          answers: shuffle(question.answers)
        }));
      }

      return questions;
    };

    const availableQuestions = ref<Raw<GameQuizQuestionState>[]>(getAvailableQuestions());

    const setAvailableQuestions = () => {
      availableQuestions.value = getAvailableQuestions();
    };

    const swipeQuestionAnswers = computed(() => {
      return questionAnswers.value.filter((item) => item.question.answerType === QuizAnswerTypes.SWIPE);
    });

    const swipeLeftAnswer = computed(() => {
      return swipeQuestionAnswers.value.filter((item) => {
        if (item.answer.length > 0) {
          return item.answer[0].id === 1;
        }
        return false;
      });
    });

    const swipeRightAnswer = computed(() => {
      return swipeQuestionAnswers.value.filter((item) => {
        if (item.answer.length > 0) {
          return item.answer[0].id === 2;
        }
        return false;
      });
    });

    const activeQuestion = computed<GameQuizQuestionState | null>(() => {
      return availableQuestions.value[activeQuestionIndex.value] ?? null;
    });

    const introductions = computed<QuizActiveQuestion[]>(() => {
      return activeQuestions.value.filter((question) => {
        return question.displayVariant === QuestionDisplayVariant.INTRODUCTION;
      });
    });

    const shownQuestions = computed<GameQuizQuestionState[]>(() => {
      return activeQuestions.value
        .filter((question) => {
          return question.displayVariant === QuestionDisplayVariant.QUESTION;
        })
        .map((question) => question.question);
    });

    /**
     * Not all answers in the questionAnswers array is from the user.
     * Some contains answers from skipped questions that has no answer.
     * These shouldn't always be accounted for in metrics, replacement tags etc.
     * This is where this computed makes sense. As it filters these out.
     */
    const userAnsweredQuestions = computed(() => {
      return questionAnswers.value.filter((item) => item.answer.length > 0);
    });

    const feedbackMessages = computed<QuizActiveQuestion[]>(() => {
      return activeQuestions.value.filter((question) => {
        return question.displayVariant === QuestionDisplayVariant.FEEDBACK;
      });
    });

    /**
     * returns the correct selected question answers
     */
    const correctQuestionAnswers = computed<GameQuizQuestionAnswer[]>(() => {
      return questionAnswers.value.filter((answerState) => {
        if (answerState.answer.length === 0) {
          return false;
        }

        return (
          answerState.answer.filter((answerItem) => answerState.question.correctAnswers.includes(answerItem.id))
            .length === answerState.answer.length
        );
      });
    });

    const incorrectQuestionAnswers = computed<GameQuizQuestionAnswer[]>(() => {
      return questionAnswers.value.filter((answerState) => {
        if (answerState.answer.length === 0) {
          return false; // Consider no answer as not incorrect for this case
        }

        return answerState.answer.some((answerItem) => !answerState.question.correctAnswers.includes(answerItem.id));
      });
    });

    const hasCorrectAnswer = computed(() => {
      if (
        !questionAnswers.value[activeQuestionIndex.value] ||
        questionAnswers.value[activeQuestionIndex.value].answer.length === 0
      ) {
        return false;
      }

      return (
        questionAnswers.value[activeQuestionIndex.value].answer.filter((answerItem) =>
          activeQuestion.value?.correctAnswers.includes(answerItem.id)
        ).length === questionAnswers.value[activeQuestionIndex.value].answer.length
      );
    });

    const quizContainerClasses = computed(() => {
      let answerTypeClass: string | undefined;
      switch (activeQuestions.value[0]?.question.answerLayout?.state.designType) {
        case AnswerDesignTypes.BULLETS:
          switch (activeQuestions.value[0]?.question.answerLayout?.state.listStyle) {
            case AnswerBulletsTypes.DISC:
              answerTypeClass = 'quiz--bullets quiz--bullets-disc';
              break;

            case AnswerBulletsTypes.DECIMAL:
              answerTypeClass = 'quiz--bullets quiz--bullets-decimal';
              break;

            case AnswerBulletsTypes.LETTER:
              answerTypeClass = 'quiz--bullets quiz--bullets-letter';
              break;
            default:
              answerTypeClass = 'quiz--bullets';
              break;
          }
          break;

        case AnswerDesignTypes.RADIOBUTTONS:
          answerTypeClass = 'quiz--radiobuttons';
          break;

        default:
          answerTypeClass = 'quiz--text';
          break;
      }

      if (activeQuestions.value[0]?.question.answerLayout?.state.checkmarkColor) {
        answerTypeClass = 'quiz--custom';
      }

      let hasActiveState = false;

      if (props.model.state.general.activeStateColor || props.model.state.general.activeStateBackgroundColor) {
        hasActiveState = true;
      }

      return {
        'quiz--has-active-state': hasActiveState,
        [`quiz--flow-align-${props.model.state.general.contentAlign}`]: props.model.state.general.contentAlign,
        [`quiz--answers-align-${props.model.state.general.answerAlignment}`]: props.model.state.general.answerAlignment,
        [answerTypeClass]: true
      };
    });

    const onQuestionAnswer = async (
      answer: GameQuizAnswerState[],
      question: GameQuizQuestionState,
      timesUp = false
    ) => {
      isAnswering.value = true;

      questionAnswers.value = questionAnswers.value.filter((answered) => answered.question.id !== question.id);
      questionAnswers.value.push({
        answer,
        question
      });

      // stop questionTimer if we need to time each question
      if (
        props.model.state.general.time.enabled &&
        props.model.state.general.time.perQuestion &&
        typeof questionTimer === 'number'
      ) {
        clearInterval(questionTimer);
        questionTimer = null;
      }

      const activeQuestionState = activeQuestions.value.find((activeState) => activeState.question.id === question.id);

      // Emit SDK question times up event, allowing external implementations to suspense the game if needed.
      if (timesUp && activeQuestionState) {
        const questionTimesUpEvent = new SdkQuizQuestionTimesUpEvent(
          new SdkQuizQuestionModel(
            activeQuestionState.question,
            activeQuestionIndex.value === availableQuestions.value.length - 1
          )
        );

        SdkGameEventsEmitter.emit('question-times-up', questionTimesUpEvent);

        await questionTimesUpEvent.waitForPromises();
      }

      if (question.feedback?.enabled && activeQuestionState) {
        if (hasCorrectAnswer.value) {
          if (question.feedback.answerMessage?.correct) {
            activeQuestionState.displayVariant = QuestionDisplayVariant.FEEDBACK;

            // Emit SDK question feedback display event, allowing external implementations
            // to peak into which feedback message is shown.
            SdkGameEventsEmitter.emit(
              'question-feedback-display',
              new SdkQuizQuestionFeedbackDisplayEvent(
                new SdkQuizQuestionModel(question, activeQuestionIndex.value === availableQuestions.value.length - 1),
                question.feedback.answerMessage.correct,
                answer.map((answer) => new SdkQuizQuestionAnswerModel(question, answer)),
                true
              )
            );

            isAnswering.value = false;
            return;
          }
        } else if (question.feedback.answerMessage?.wrong) {
          activeQuestionState.displayVariant = QuestionDisplayVariant.FEEDBACK;

          // Emit SDK question feedback display event, allowing external implementations
          // to peak into which feedback message is shown.
          SdkGameEventsEmitter.emit(
            'question-feedback-display',
            new SdkQuizQuestionFeedbackDisplayEvent(
              new SdkQuizQuestionModel(question, activeQuestionIndex.value === availableQuestions.value.length - 1),
              question.feedback.answerMessage.wrong,
              answer.map((answer) => new SdkQuizQuestionAnswerModel(question, answer)),
              false
            )
          );

          isAnswering.value = false;
          return;
        }
      }

      isAnswering.value = false;

      onNextQuestion(question);
    };

    const onNextQuestion = (question: GameQuizQuestionState) => {
      utilityStore.removeBodyClass(`current-game-step-${activeQuestionIndex.value + 1}`);

      // if time challenge for each question, then we need to reset questionTimer each time we get a new question
      if (props.model.state.general.time.enabled && props.model.state.general.time.perQuestion) {
        timeLeft.value = props.model.state.general.time.limit ? props.model.state.general.time.limit : 0;
        questionTimer = window.setInterval(handleTimerPerQuestion, 1000);
      }

      // get the answers for this question
      const questionAnswer = questionAnswers.value.find((answerState) => {
        return answerState.question.id === question.id;
      });

      // we cant have multiple selected answers & answer action
      // so if we got action then it will be in the first item
      let answerAction: GameQuizAnswerState | undefined;
      if (questionAnswer && questionAnswer.answer && questionAnswer.answer[0]) {
        answerAction = questionAnswer?.question.answers.find((question) => question.id === questionAnswer.answer[0].id);
      }

      // check if we got action for the question answer and do something if we got it
      // we cant have an action if "question.enableMultipleAnswers" is enabled bc then skip logic is disabled for the question
      if (
        props.model.state.general.enableSkipLogic &&
        !question.enableMultipleAnswers &&
        answerAction &&
        (answerAction.actionPage || answerAction.action)
      ) {
        handleQuestionAction(answerAction);
        return;
      }

      const nextQuestion = availableQuestions.value[activeQuestionIndex.value + 1];

      if (!nextQuestion || !nextQuestion.backgroundImage.enabled) {
        campaignStore.gameSectionImageOverlay = undefined;
      }

      handleEndGame();

      if (!gameOver.value && activeQuestionIndex.value >= 0) {
        activeQuestionIndex.value++;

        // step has to incremenet on last item aswell
      }
    };

    // handles the quiz skip logic
    // can allow answer to go to another flow page
    // or to a specific question number
    const handleQuestionAction = (answer: GameQuizAnswerState) => {
      if (gameOver.value) return;
      // go to specific flow page
      if (answer.actionPage) {
        const won = answer.action === 'winner';

        gameEndTime.value = Date.now();
        gameOver.value = true;

        clearTimers();
        useEndGame(won, answer.actionPage);
        return;
      }

      // go to specific question
      if (typeof answer.action === 'string' && answer.action !== 'winner' && answer.action !== 'loser') {
        // need to get the number values from string e.g. action 'question_4' to 4
        // so we can use it to send the user to question 4
        const parseQuestionKey = parseInt(answer.action.replace(/^\D+/g, ''), 10);
        const nextQuestionIndex = parseQuestionKey - 1;

        // Skip logic cuases index'es to go out of bounds, because we skip questions.
        // We can only skip ahead, and not go back in the list. So we fill in empty answers
        // for the questions that we skipped.
        for (let i = activeQuestionIndex.value + 1; i < nextQuestionIndex; i++) {
          questionAnswers.value.push({
            answer: [],
            question: availableQuestions.value[Number(i)]
          });
        }

        activeQuestionIndex.value = nextQuestionIndex;
      }

      if (answer.action === 'winner') {
        gameEndTime.value = Date.now();
        gameOver.value = true;

        clearTimers();
        useEndGame(true);
      }

      if (answer.action === 'loser') {
        gameEndTime.value = Date.now();
        gameOver.value = true;

        clearTimers();
        useEndGame(false);
      }
    };

    // introductions comes before each question
    const onCloseIntroduction = (questionId: number) => {
      const activeQuestionState = activeQuestions.value.find((activeState) => activeState.question.id === questionId);

      if (activeQuestionState) {
        activeQuestionState.displayVariant = QuestionDisplayVariant.QUESTION;
      }
    };

    const onStopTimer = () => {
      if (props.model.state.general.time.enabled && props.model.state.general.time.perQuestion) {
        if (questionTimer) {
          clearInterval(questionTimer);
          questionTimer = null;
        }
      }
    };

    const onPreviousQuestion = (questionId: number) => {
      if (props.model.state.general.time.enabled && props.model.state.general.time.perQuestion) {
        if (questionTimer) {
          clearInterval(questionTimer);
          questionTimer = null;
        }

        timeLeft.value = props.model.state.general.time.limit ? props.model.state.general.time.limit : 0;
        questionTimer = window.setInterval(handleTimerPerQuestion, 1000);
      }

      const activeQuestionState = activeQuestions.value.find((activeState) => activeState.question.id === questionId);
      if (activeQuestionState && activeQuestionState.displayVariant === QuestionDisplayVariant.FEEDBACK) {
        activeQuestionState.displayVariant = QuestionDisplayVariant.QUESTION;
      } else if (activeQuestionIndex.value > 0) {
        activeQuestionIndex.value--;
      } else if (activeQuestionState) {
        activeQuestionState.displayVariant = QuestionDisplayVariant.INTRODUCTION;
      }
    };

    const clearTimers = () => {
      if (questionTimer) {
        clearInterval(questionTimer);
        questionTimer = null;
      }
      if (gameTimer) {
        clearInterval(gameTimer);
        gameTimer = null;
      }
    };

    const handleEndGame = () => {
      // when game force stepguide to add 1 more step to align with totalSteps
      if (gameOver.value) return;

      let won = true;
      // if "setting" force loser page on incorrect answer
      if (props.model.state.general.loserOnIncorrectAnswer && !hasCorrectAnswer.value) {
        won = false;
        gameEndTime.value = Date.now();
        gameOver.value = true;
        clearTimers();
        useEndGame(won);
        return;
      }

      // if no more questions -> then end game
      if (activeQuestionIndex.value === availableQuestions.value.length - 1) {
        if (props.model.state.general.correctAnswersRequired && props.model.state.general.correctAnswersRequired > 0) {
          won = correctQuestionAnswers.value.length >= props.model.state.general.correctAnswersRequired;
        }
        gameOver.value = true;
        gameEndTime.value = Date.now();

        clearTimers();
        useEndGame(won);
      }
    };

    const handleTimerPerQuestion = () => {
      if (!gameOver.value) {
        timeLeft.value--;

        if (timeLeft.value === 0) {
          if (props.model.state.general.loserOnIncorrectAnswer) {
            gameOver.value = true;
            gameEndTime.value = Date.now();

            clearTimers();
            useEndGame(false);
            return;
          }

          // if questionTimer for each question
          // submit a wrong answer and go to next question
          if (props.model.state.general.time.perQuestion) {
            const inCorrectAnswer: GameQuizAnswerState[] = [];
            // show next question
            if (activeQuestion.value) {
              onQuestionAnswer(inCorrectAnswer, activeQuestion.value, true);
            }
          }
        }
      }

      if (!gameOver.value && timeUsed.value !== props.model.state.general.time.limit) {
        if (timeUsed.value === props.model.state.general.time.limit) {
          // if questionTimer for each question
          // submit a wrong answer and go to next question
          if (props.model.state.general.time.perQuestion) {
            const inCorrectAnswer: GameQuizAnswerState[] = [];
            // show next question
            if (activeQuestion.value) {
              onQuestionAnswer(inCorrectAnswer, activeQuestion.value, true);
            }
          }
        }
      }
    };

    const handleEndTimer = () => {
      if (gameOver.value) return;
      gameOver.value = true;

      let won = false;
      if (props.model.state.general.correctAnswersRequired && props.model.state.general.correctAnswersRequired > 0) {
        won = correctQuestionAnswers.value.length >= props.model.state.general.correctAnswersRequired;
      }

      clearTimers();

      gameEndTime.value = Date.now();

      if (!campaignStore.model?.state.isEditModeActive) {
        useEndGame(won);
      }
    };

    const handleGameTimer = () => {
      if (!gameOver.value) {
        // only update timeUsed when question is active
        timeUsed.value++;
        if (!props.model.state.general.time.perQuestion) {
          timeLeft.value--;
        }

        if (
          props.model.state.general.time.enabled &&
          !props.model.state.general.time.perQuestion &&
          props.model.state.general.time.limit
        ) {
          if (timeUsed.value >= props.model.state.general.time.limit) {
            handleEndTimer();
          } else if (timeLeft.value <= 0) {
            handleEndTimer();
          }
        }
      }
    };

    const addRepeatableAnswers = (
      type: GameQuizRepeatableType,
      condition?: (answerState: GameQuizQuestionAnswer) => boolean
    ) => {
      registerRepeatable(type, async () => {
        let repeatableAnswers = userAnsweredQuestions.value as GameQuizQuestionAnswer[];

        if (condition) {
          repeatableAnswers = repeatableAnswers.filter(condition);
        }

        return repeatableAnswers.map((answerState) => {
          const answerText = answerState.answer.map((answer) => {
            if (answer.text.includes('/files')) {
              return `<img src="${answer.text}" style="max-width: 100px;" />`;
            }

            return answer.text.trim();
          });

          const descriptionText = answerState.answer.map((answer) => {
            return answer.description?.trim() ?? '';
          });

          const correctAnswers = answerState.question.answers
            .filter((answer) => answerState.question.correctAnswers.includes(answer.id))
            .map((answer) => `${answer.text.trim()}`);

          return {
            id: answerState.question.id + answerText.join(',').toString(),
            question: answerState.question.question,
            ...(answerState.question.questionString && {
              question_sorting: answerState.question.questionString
            }),
            question_image: answerState.question.questionImage ?? '',
            answer:
              answerState.question.answerType === QuizAnswerTypes.IMAGE
                ? answerText.join(' ').toString()
                : answerText.join(', ').toString(),
            correct_answer: correctAnswers.join(', '),
            answer_additional_description: descriptionText.join(', ').toString()
          };
        });
      });
    };

    addRepeatableAnswers('all_answers');
    addRepeatableAnswers('correct_answers', (answerState) => {
      return !!correctQuestionAnswers.value.find(
        (correctAnswerState) => correctAnswerState.question.id === answerState.question.id
      );
    });

    addRepeatableAnswers('wrong_answers', (answerState) => {
      return !correctQuestionAnswers.value.find(
        (correctAnswerState) => correctAnswerState.question.id === answerState.question.id
      );
    });

    const timeusedMs = computed(() => (gameEndTime.value ?? Date.now()) - (gameStartTime.value ?? Date.now()));

    const replacementTags = computed<ReplacementTags>(() => {
      const replacementTagData: ReplacementTags = {
        timeused: timeUsed.value,
        timeused_ms: timeusedMs.value,
        timeleft: !props.model.state.general.time.perQuestion ? timeLeft.value : '',
        total_answers: userAnsweredQuestions.value.length,
        wrong_answers: incorrectQuestionAnswers.value.length,
        correct_answers: correctQuestionAnswers.value.length,
        answers_additional: buildAnswersAdditional(userAnsweredQuestions.value),
        flow_output: buildAnswersFlowOutput(userAnsweredQuestions.value),
        result_output: buildResultOutput(props.model.state, userAnsweredQuestions.value)
      };

      userAnsweredQuestions.value.forEach((answerState) => {
        replacementTagData[`question_${answerState.question.questionNumber}_question`] = answerState.question.question;
        replacementTagData[`question_${answerState.question.id}_question`] = answerState.question.question;
        replacementTagData[`question_${answerState.question.id}`] = answerState.question.question;
        answerState.answer.forEach((answer) => {
          const feedback = hasCorrectAnswer.value
            ? answerState.question.feedback.answerMessage?.correct
            : answerState.question.feedback.answerMessage?.wrong;

          if (answerState.question.enableMultipleAnswers) {
            const existingValue = replacementTagData[`question_${answerState.question.id}_answer`];
            const existingValueNumber = replacementTagData[`question_${answerState.question.questionNumber}_answer`];
            if (existingValue) {
              replacementTagData[`question_${answerState.question.id}_answer`] = `${existingValue}, ${answer.text}`;
              replacementTagData[`question_${answerState.question.questionNumber}_answer`] =
                `${existingValueNumber}, ${answer.text}`;
            } else {
              replacementTagData[`question_${answerState.question.id}_answer`] = answer.text;
              replacementTagData[`question_${answerState.question.questionNumber}_answer`] = answer.text;
            }
          } else {
            replacementTagData[`question_${answerState.question.id}_answer`] = answer.text;
            replacementTagData[`question_${answerState.question.questionNumber}_answer`] = answer.text;
          }

          if (feedback) {
            replacementTagData[`question_${answerState.question.id}_feedback`] = feedback;
          }
        });
      });
      return replacementTagData;
    });

    const metrics = computed<MetricData>(() => {
      const metricData: MetricData = {
        timeused: timeUsed.value,
        timeused_ms: timeusedMs.value,
        timeleft: Math.max(0, timeLeft.value),
        total_answers: userAnsweredQuestions.value.length,
        wrong_answers: incorrectQuestionAnswers.value.length,
        correct_answers: correctQuestionAnswers.value.length
      };

      userAnsweredQuestions.value.forEach((answerState) => {
        answerState.answer.forEach((answer) => {
          if (!answer.text) return;
          if (answerState.question.enableMultipleAnswers) {
            metricData[`question_${answerState.question.id}_answer_${answer.id}`] = 1;
          } else {
            metricData[`question_${answerState.question.id}`] = `answer_${answer.id}`;
          }
        });
      });
      return metricData;
    });

    const startGame = () => {
      if (campaignStore.hasGamePopover) return;
      if (props.model.state.general.time.enabled && props.model.state.general.time.perQuestion) {
        questionTimer = window.setInterval(handleTimerPerQuestion, 1000);
      }

      gameStartTime.value = Date.now();
      gameTimer = window.setInterval(handleGameTimer, 1000);
    };

    watch(
      metrics,
      () => {
        campaignStore.addReplacementTags(replacementTags.value);
        campaignStore.metricData = { ...campaignStore.metricData, ...metrics.value };
      },
      { immediate: true }
    );

    watch(
      activeQuestion,
      () => {
        if (!activeQuestion.value) {
          activeQuestions.value = [];
          return;
        }

        activeQuestions.value = [
          {
            displayVariant: activeQuestion.value.introduction.enabled
              ? QuestionDisplayVariant.INTRODUCTION
              : QuestionDisplayVariant.QUESTION,
            question: activeQuestion.value
          }
        ];
      },
      {
        immediate: true
      }
    );

    watch(activeQuestionIndex, (val, oldVal) => {
      if (val !== oldVal) {
        stepIndex.value = val;
      }
    });

    watch(
      () => props.model.state.questions.length,
      () => {
        setAvailableQuestions();
      }
    );

    onBeforeUnmount(() => {
      utilityStore.removeBodyClass(`current-game-step-${activeQuestionIndex.value + 1}`);
      campaignStore.gameSectionImageOverlay = undefined;

      if (questionTimer) {
        clearInterval(questionTimer);
        questionTimer = null;
      }

      if (gameTimer) {
        clearInterval(gameTimer);
        gameTimer = null;
      }
    });

    watch(
      () => campaignStore.hasGamePopover,
      () => {
        startGame();
      }
    );

    watch(
      stepIndex,
      (newIndex) => {
        if (!gameOver.value) {
          gameStepsStore.currentStep = newIndex;
        }
      },
      { immediate: true }
    );

    watch(
      availableQuestions,
      () => {
        gameStepsStore.totalSteps = availableQuestions.value.length;
      },
      { immediate: true }
    );

    registerRepeatable('left_swipe_output', async () => {
      return swipeLeftAnswer.value.map<RepeatableData>((item) => {
        return {
          id: item.question.id,
          image: item.question.swipeImage?.src ?? ''
        };
      });
    });

    registerRepeatable('right_swipe_output', async () => {
      return swipeRightAnswer.value.map<RepeatableData>((item) => {
        return {
          id: item.question.id,
          image: item.question.swipeImage?.src ?? ''
        };
      });
    });

    watch(swipeLeftAnswer, () => updateRepeatable('left_swipe_output'));
    watch(swipeRightAnswer, () => updateRepeatable('right_swipe_output'));

    return {
      correctQuestionAnswers,
      feedbackMessages,
      questionAnswers,
      hasCorrectAnswer,
      shownQuestions,
      activeQuestions,
      introductions,
      activeQuestionIndex,
      activeQuestion,
      quizContainerClasses,
      availableQuestions,
      gameOver,
      isAnswering,
      onStopTimer,
      clearTimers,
      onQuestionAnswer,
      onCloseIntroduction,
      onNextQuestion,
      onPreviousQuestion,
      QuizAnswerTypes,
      onAfterEnter: () => {
        /**
         * Init the game after the flow animation is done, to make sure we can get the correct container size.
         * if rendered while animation is running it can get miscalculated container size.
         * Also fix timing issue with game starting before the animation is done between flow pages
         */
        startGame();
      }
    };
  }
});
</script>

<style>
.quiz {
  position: relative;
}

.quiz .question__wrapper {
  margin-left: -0.2rem;
  margin-bottom: 1.5rem;
  margin-top: -0.5rem;
}

.quiz .question__message-answer {
  text-align: left;
}

.quiz .content,
.quiz .question__wrapper,
.quiz .question__content {
  text-align: center;
}

.quiz .question .question__message img {
  display: inline-block;
}

.quiz.quiz--flow-align-left .question__content-image,
.quiz.quiz--flow-align-left .content,
.quiz.quiz--flow-align-left .question__wrapper,
.quiz.quiz--flow-align-left .question__content,
.quiz.quiz--flow-align-left .question__message-answer {
  text-align: left;
}

.quiz.quiz--flow-align-right .question__content-image,
.quiz.quiz--flow-align-right .content,
.quiz.quiz--flow-align-right .question__wrapper,
.quiz.quiz--flow-align-right .question__content,
.quiz.quiz--flow-align-right .question__message-answer {
  text-align: right;
}

.quiz.quiz--flow-align-left .question__content .question__video,
.quiz.quiz--flow-align-left .question__answers .question__answer-item {
  margin-left: 0 !important;
}

.quiz.quiz--flow-align-right .question__content .question__video,
.quiz.quiz--flow-align-right .question__answers .question__answer-item {
  margin-right: 0 !important;
}

.quiz.quiz--flow-align-center .question__content-image,
.quiz.quiz--flow-align-center .content,
.quiz.quiz--flow-align-center .question__wrapper,
.quiz.quiz--flow-align-center .question__content,
.quiz.quiz--flow-align-center .question__message-answer {
  text-align: center;
}

.quiz .question__content-image img,
.quiz .question__wrapper img {
  display: inline-block;
}

.quiz .question__image {
  max-width: 30rem;
  backface-visibility: hidden;
  display: inline-block;
  position: relative;
  vertical-align: top;
  margin: 1.5rem 0;
  padding: 0 1.5rem;

  .question__additional-description {
    cursor: pointer;
  }
}

.quiz .question__additional-description {
  font-size: 0.75em;
  display: block;
  width: 100%;
  text-align: center;
  padding: 0 10px;
  cursor: default;
  transition:
    transform 0.15s ease-out,
    opacity 0.15s linear;
}

.quiz .question__video {
  display: block;
  width: 45rem;
  max-width: 100%;
  margin: 0 auto 3rem auto;
  backface-visibility: hidden;
}

.site--desktop .quiz .question__content.question__content--has-video-left .question__video {
  width: 46%;
  max-width: 46%;
  float: left;
}

.site--desktop .quiz .question__content.question__content--has-video-right .question__video {
  width: 46%;
  max-width: 46%;
  float: right;
}

.site--mobile .quiz .question__content.question__content--has-video-above:after,
.site--mobile .quiz .question__content.question__content--has-video-left:after,
.site--mobile .quiz .question__content.question__content--has-video-right:after {
  content: '';
  display: table;
  clear: both;
}

.site--desktop .quiz .question__content.question__content--has-video-right .question__wrapper,
.site--desktop .quiz .question__content.question__content--has-video-left .question__wrapper {
  margin-top: 10px;
}

.site--desktop .quiz .question__content.question__content--has-video-right .question__wrapper {
  margin-right: 50%;
}

.site--desktop .quiz .question__content.question__content--has-video-left .question__wrapper {
  margin-left: 50%;
}

.site--desktop .quiz .question__content.question__content--has-video-left .question__answers,
.site--desktop .quiz .question__content.question__content--has-video-right .question__answers {
  clear: both;
}

.site--mobile .quiz .question__content.question__content--has-video-above .question__video,
.site--mobile .quiz .question__content.question__content--has-video-left .question__video,
.site--mobile .quiz .question__content.question__content--has-video-right .question__video {
  width: 100% !important;
}

.quiz .question__answers {
  animation-duration: 0.8s;
}

.quiz .question--disabled:not(.question--awaiting-continue) .content__item-action {
  opacity: 0.2;
  pointer-events: none;
}

.quiz .question--disabled .question__answers .question__answer-item,
.quiz .question--disabled .question__answers.question__answers--slider {
  pointer-events: none;
}

.quiz .question__answer-item {
  font-weight: normal;
}

.quiz .question__answers .question__answer-item {
  transform: scale(1);
  transform-origin: center center;
  margin: 0 -1rem;
  padding: 0.3rem 1rem;
  position: relative;
  text-align: center;
  transition:
    opacity 150ms linear,
    color 150ms linear,
    background-color 150ms linear;
}

.quiz .question .question__answers:not(.question__answers--images) .question__answer-item {
  margin: 0 auto;
}

.quiz .question__answers .question__answer-item {
  transition: all 150ms linear !important;
}

.quiz .question__answers .question__answer-item .question__answer-item-wrapper {
  position: relative;
  display: inline-block;
  max-width: calc(100% - 3rem);
  cursor: pointer;
  opacity: 0.5;
  line-height: 1.2em;
  padding: 0.2rem;
  transition: all 0.15s linear;
}

.quiz.quiz--radiobuttons .question__answers .question__answer-item-wrapper,
.quiz.quiz--custom .question__answers .question__answer-item-wrapper {
  padding: 0.2rem 0.2rem 0.2rem 3.4rem;
}

.quiz .question__answers .question__answer-item .question__answer-item-wrapper > span {
  line-height: 1.2em;
}

.quiz .question__answers.question__answers--images .question__answer-item {
  cursor: pointer;
  opacity: 0.5;
}

.quiz.quiz--has-active-state .question__answers.question__answers--images .question__answer-item,
.quiz.quiz--has-active-state .question__answers .question__answer-item .question__answer-item-wrapper {
  opacity: 1;
}

.quiz .question .question-answers.question__answers--images .question__image {
  max-width: 29%;
  margin: 1.5rem 1%;
  display: inline-block;
  backface-visibility: hidden;
}

.quiz .question__answers.question__answers--images .question__image .question__answer-item {
  padding: 0;
  margin: 0 auto;
}

.quiz .question.question--answers-4 .question__answers.question__answers--images .question__image {
  max-width: 22%;
}

.quiz .question .question__answers.question__answers--images.question__answers--images-2,
.quiz .question .question__answers.question__answers--images.question__answers--images-desktop-1,
.quiz .question .question__answers.question__answers--images.question__answers--images-desktop-2,
.quiz .question .question__answers.question__answers--images.question__answers--images-desktop-3 {
  display: inline-block;
  width: auto;
}

.quiz .question .question__answers.question__answers--images.question__answers--images-2 .question__image,
.quiz .question .question__answers.question__answers--images.question__answers--images-desktop-1 .question__image,
.quiz .question .question__answers.question__answers--images.question__answers--images-desktop-2 .question__image,
.quiz .question .question__answers.question__answers--images.question__answers--images-desktop-3 .question__image {
  max-width: 100%;
  margin: 1.5rem 0;
  padding: 0 1.5rem;
  float: left;
}

.quiz .question .question__answers.question__answers--images.question__answers--images-1 .question__image,
.quiz .question .question__answers.question__answers--images.question__answers--images-desktop-1 .question__image {
  float: none;
  width: 100%;
  max-width: 100%;
}

.quiz .question .question__answers.question__answers--images.question__answers--images-1 .question__image {
  max-width: 100%;
}

.quiz .question .question__answers.question__answers--images.question__answers--images-desktop-2 .question__image {
  max-width: 50%;
}

.quiz .question .question__answers.question__answers--images.question__answers--images-desktop-3 .question__image {
  width: 33.33%;
}

.quiz .question .question__answers.question__answers--images.question__answers--images-2 .question__image {
  clear: none;
  width: 50%;
}

.quiz
  .question
  .question__answers.question__answers--images.question__answers--images-2
  .question__image:nth-child(2n + 1),
.quiz
  .question
  .question__answers.question__answers--images.question__answers--images-desktop-2
  .question__image:nth-child(2n + 1),
.quiz
  .question
  .question__answers.question__answers--images.question__answers--images-desktop-3
  .question__image:nth-child(3n + 1),
.quiz
  .question
  .question__answers.question__answers--videos.question__answers--images-2
  .question__video-item:nth-child(2n + 1),
.quiz
  .question
  .question__answers.question__answers--videos.question__answers--images-desktop-2
  .question__video-item:nth-child(2n + 1),
.quiz
  .question
  .question__answers.question__answers--videos.question__answers--images-desktop-3
  .question__video-item:nth-child(3n + 1) {
  clear: both;
}

.quiz .question__answers.question__answers--images .question__image,
.quiz .question__answers.question__answers--images .question__image .question__answer-item {
  transition: all 150ms linear !important;
}

.quiz .question__answers.question__answers--images .question__image.question__image--correct .question__answer-item {
  opacity: 0.5 !important;
}

.quiz .question__answers.question__answers--images .question__image.question__image--wrong .question__answer-item {
  opacity: 0.5 !important;
}

.quiz .question__answers.question__answers--images .question__image .question__answer-item {
  opacity: 0.5;
  transition: opacity 150ms linear;
}

.site--desktop .quiz .question__answers.question__answers--images .question__image:hover .question__answer-item,
.quiz
  .question__answers.question__answers--images
  .question__image
  .question__answer-item.question__answer-item--selected {
  opacity: 1;
}

.quiz .question__answers.question__answers--images .question__image .question__answer-item {
  backface-visibility: hidden;
}

.site--desktop .quiz .question__answers.question__answers--images .question__answer-item:hover,
.site--desktop .quiz .question__answers .question__answer-item .question__additional-description:hover,
.quiz .question__answers .question__answer-item.question__answer-item--selected .question__additional-description {
  opacity: 1;
}

.quiz .question__answers .question__answer-item.question__answer-item--selected .question__answer-item-wrapper,
.site--desktop .quiz .question__answers .question__answer-item .question__answer-item-wrapper:hover {
  opacity: 1;
}

.quiz .question__link {
  display: block;
  margin-top: 1.5rem;
}

.quiz .question--has-error .content__item-action.content__item-action--disabled {
  transform: translate(0, 100%);
  opacity: 0;
}

.quiz .question--has-error .content__item-action .content__item-action,
.quiz .question--has-error .content__item-action-error {
  transition:
    transform 0.3s cubic-bezier(0.2, 0.1, 0.07, 1),
    opacity 0.2s linear;
}

.quiz .content__item-action .content__item-action {
  transform: translate(0, 0%);
  opacity: 1;
  position: relative;
}

.quiz .content__item-action-error {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quiz .content__item-action-error.ng-enter {
  transform: translate(0, -100%);
  opacity: 0;
}

.quiz .content__item-action-error.ng-enter-active {
  transform: translate(0, 0%);
  opacity: 1;
}

.quiz .content__item-action-error.ng-leave {
  transform: translate(0, 0%);
  opacity: 1;
}

.quiz .content__item-action-error.ng-leave-active {
  transform: translate(0, -100%);
  opacity: 0;
}

.quiz.quiz--bullets-letter .question__answers .question__answer-item-wrapper,
.quiz.quiz--bullets-decimal .question__answers .question__answer-item-wrapper {
  vertical-align: top;
}

.quiz.quiz--bullets-decimal .question__answers {
  counter-reset: num;
}

.quiz.quiz--bullets-decimal .question__answers .question__answer-item {
  counter-increment: num;
}

.quiz.quiz--bullets-decimal .question__answers .question__answer-item-wrapper:before {
  content: counter(num) '. ';
  line-height: 1.2em;
  vertical-align: top;
}

.quiz.quiz--bullets-letter .question__answers {
  counter-reset: letter;
}
.quiz.quiz--bullets-letter .question__answers .question__answer-item {
  counter-increment: letter;
}

.quiz.quiz--bullets-letter .question__answers .question__answer-item-wrapper:before {
  content: counter(letter, upper-alpha) '. ';
  line-height: 1.2em;
  vertical-align: top;
}

.quiz.quiz--bullets-disc .question__answers .question__answer-item-wrapper:before {
  content: '\f111';
  display: inline-block;
  font-family: FontAwesome;
  font-weight: normal;
  font-style: normal;
  vertical-align: middle;
  margin-right: 1rem;
  margin-left: 2px;
  font-size: 1.4rem;
}

.quiz.quiz--answers-align-left .question__answers .question__answer-item,
.quiz.quiz--flow-align-left .question__answer-item {
  text-align: left;
}

.quiz.quiz--answers-align-center .question__answers .question__answer-item,
.quiz.quiz--flow-align-center .question__answer-item {
  text-align: center;
}

.quiz.quiz--answers-align-right .question__answers .question__answer-item,
.quiz.quiz--flow-align-right .question__answer-item {
  text-align: right;
}

.quiz .question__answers .question__answer-item .question__answer-item-radio {
  position: relative;
  box-sizing: border-box;
  display: inline-block;
  border-radius: 50%;
  width: 20px;
  min-width: 20px;
  height: 20px;
  margin: -4px 10px 0 -30px;
  text-align: center;
  background: #fafafa;
  vertical-align: middle;
}

.quiz .question__answers .question__answer-item .question__answer-item-radio > .question__answer-item-radio-icon {
  content: '';
  box-sizing: border-box;
  position: absolute;
  top: 50%;
  left: 50%;
  margin: -7px 0 0 -7px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  transform: scale(0);
  transform-origin: 50% 50%;
  background: #292828;
  opacity: 0;
  transition:
    opacity 150ms linear,
    transform 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

.quiz
  .question__answers
  .question__answer-item.question__answer-item--selected
  .question__answer-item-radio
  > .question__answer-item-radio-icon {
  transform: scale(1);
  opacity: 1;
}

.quiz .question__answer-item .question__answer-item-checkmark {
  position: relative;
  display: inline-block;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  line-height: 1.6em;
  margin: -0.4rem 1rem 0 -3rem;
  text-align: center;
  font-size: 1.4rem;
  vertical-align: middle;
}

/* Custom, iPhone Retina */
@media only screen and (max-width: 480px), screen and (max-height: 600px) {
  .site--mobile .quiz .question__answers .question__answer-item {
    margin-bottom: 1rem;
  }
}

@media only screen and (max-height: 700px) {
  .site--desktop .quiz .question__content.question__content--has-video:after {
    content: '';
    display: table;
    clear: both;
  }

  .site--desktop .quiz .question__content.question__content--has-video .question__video {
    width: 46%;
    float: left;
  }

  .site--desktop .quiz .question__content.question__content--has-video .question__wrapper {
    margin-top: 10px;
  }

  .site--desktop .quiz .question__content.question__content--has-video .question__wrapper {
    width: 50%;
    float: right;
    margin-left: 0;
    margin-right: 0;
  }

  .site--desktop .quiz .question__content.question__content--has-video .question__answers,
  .site--desktop .quiz .question__content.question__content--has-video .question__answers {
    clear: both;
  }
}

.game-info {
  text-align: center;
  margin: 1.5rem 0;
}

.game-info__item {
  display: block;
  position: relative;
  text-align: center;
}

.question .question__answers.question__answers--slider {
  display: block;
  padding-bottom: 5rem;
}

.quiz .question .question__answers--swipe {
  margin-top: 25px;
  margin-bottom: 25px;
}

.quiz .question .question__answers--swipe img {
  margin: 0;
  max-width: 100%;
}

.site--mobile .quiz .question .question__answers--swipe img {
  max-width: 100%;
}

.quiz .question .question__answers--swipe .question__answer-item {
  text-align: center;
}

.quiz .question .question__answers--swipe .question__answer-item-card {
  display: inline-block;
  position: relative;
}

.quiz .question .question__answers--swipe .question__swipe-indicator {
  display: block;
  position: absolute;
  top: 15px;
  left: 15px;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: #383838;
  line-height: 45px;
  font-size: 20px;
  color: #fff;
  z-index: 1;
  opacity: 0;
  transition:
    opacity 75ms linear,
    transform 100ms ease-out;
  transform: scale(0.5) translateZ(0);
}

.quiz .question .question__answers--swipe .question__swipe-indicator--left {
  left: auto;
  right: 20px;
}

.quiz
  .question
  .question__answers--swipe
  .question__answer-item
  .question__answer-item--swipe-left
  .question__swipe-indicator--left {
  opacity: 1;
  transform: scale(1) translateZ(0);
}

.quiz
  .question
  .question__answers--swipe
  .question__answer-item
  .question__answer-item--swipe-right
  .question__swipe-indicator--right {
  opacity: 1;
  transform: scale(1) translateZ(0);
}

.quiz .question .question__answers--swipe .question__answer-item--swiped .question__answer-item-card {
  opacity: 0;
}

.quiz .question .question__answers .question__answer-item-percentage {
  display: block;
  position: absolute;
  top: 50%;
  right: 15px;
  min-height: 0 !important;
  margin-top: -1px;
  line-height: 1em !important;
  transform: translateY(-50%);
  font-size: 0.7em;
  z-index: 1;
  opacity: 0;
  transition: all 200ms linear !important;
}

.question__answers.question__answers--images .question__answer-item-percentage,
.question__answers.question__answers--slider .question__answer-item-percentage {
  background: black;
}

.quiz .question .question__answers.question__answers--images .question__answer-item-percentage,
.quiz .question .question__answers.question__answers--slider .question__answer-item-percentage {
  width: 3.5em;
  height: 3.5em;
  top: auto;
  bottom: 0;
  right: 0;
  border-radius: 50%;
  transform: scale(0.5) translate(30%, 30%);
  transform-origin: 80% 80%;
  font-size: 0.6em;
  text-align: center;
  font-weight: normal;
  letter-spacing: 0;
  line-height: 3.5em !important;
  transition:
    opacity 100ms linear,
    transform 400ms cubic-bezier(0.215, 0.61, 0.355, 1) !important;
}

.quiz .question .question__answers.question__answers--slider .question__answer-item-percentage {
  top: 50%;
  left: 100%;
  margin: 0 0 0 8px;
  bottom: auto;
  right: auto;
  transform: scale(0.5) translateY(-48%);
  transform-origin: 50% 0%;
  font-size: 0.75em;
}

.quiz
  .question.question--result
  .question__answers.question__answers--slider
  .question__answer-item-percentage
  .question__answer-item-percentage-value {
  position: static;
  display: inline;
  white-space: normal;
}

.quiz .question .question__answers .question__answer-item-progress {
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.15);
  z-index: 0;
  opacity: 0;
  transition:
    all 100ms linear,
    width 1200ms cubic-bezier(0.215, 0.61, 0.355, 1) !important;
}

.quiz .question:not(.question--result) .question__answers .question__answer-item-progress {
  width: 0 !important;
}

.quiz .question.question--result .question__answers .question__answer-item-percentage,
.quiz .question.question--result .question__answers .question__answer-item-progress {
  opacity: 1;
}

.quiz .question.question--result .question__answers.question__answers--images .question__answer-item-percentage {
  transform: scale(1) translate(30%, 30%);
}

.quiz .question.question--result .question__answers.question__answers--slider .question__answer-item-percentage {
  transform: scale(1) translateY(-48%);
}

.quiz .question .content__item-action.content__item-action--previous {
  margin-right: 10px;
  float: right;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 125ms linear;
}

.site--desktop .quiz .question .content__item-action.content__item-action--previous:hover {
  opacity: 1;
}

.quiz .question .content__item-action.content__item-action--previous .fa {
  width: 0;
  text-indent: -0.4em;
}

.quiz .question .question__back-wrapper {
  display: inline-block;
  vertical-align: top;
  width: 0;
}

.quiz .question .question__back {
  display: block;
  margin-top: 15px;
  font-size: 12px;
  text-decoration: underline;
  text-align: center;
  cursor: pointer;
  transition: color 125ms linear;
}
</style>
